'use client'

import Image from 'next/image'
import { ArrowRight, Clock3, MapPin } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { dateInTimeZone } from '@/lib/zoned-date'
import { BookingSheet } from './booking-sheet'
import { createBookingLaunch } from './booking-launch'
import { EventCheckoutSheet } from './event-checkout-sheet'
import { FavoriteVenueButton } from './favorite-venue-button'
import { PrivateEventInquiry } from './private-event-inquiry'
import { PublicVenueDetails } from './public-venue-details'
import { RestaurantProfileView } from './restaurant-profile-view'
import { GuestStepper, ReservationDatePicker } from './reservation-controls'
import type { AvailabilityOption, BookingIntent, PublicEvent, PublicOffering, PublicVenue, PublicVenueState, Venue } from './domain'
import { defaultVenueImage as venueImage, toBookingVenue } from './venue-presentation'
import { getVenueOpenStatus } from './venue-status'
import { useFavoriteLocations } from './use-favorite-locations'
import { externalBookingChannel } from './venue-source'
import { createDiscoverySearchDefaults } from './search-defaults'

export function VenueDiscovery({ venueState, bookingIntent, bookingOnly = false, profile = false }: { venueState: PublicVenueState; bookingIntent?: BookingIntent; bookingOnly?: boolean; profile?: boolean }) {
  if (venueState.status === 'unavailable') {
    if (bookingOnly) return null
    return (
      <section className="bookable-section" id="restaurants">
        <div className="section-heading section-heading--compact">
          <div><p className="eyebrow">Reservations</p><h2>Find your next table.</h2></div>
        </div>
        <div className="public-unavailable" role="status"><Clock3 /><div><strong>Reservations are taking a quick pause.</strong><p>{venueState.message}</p></div></div>
      </section>
    )
  }

  const external = externalBookingChannel(venueState.venue)
  return external
    ? <ExternalConnectedVenue venue={venueState.venue} bookingOnly={bookingOnly} profile={profile} external={external} />
    : <ConnectedVenue venue={venueState.venue} bookingIntent={bookingIntent} bookingOnly={bookingOnly} profile={profile} />
}

function ExternalConnectedVenue({
  venue,
  bookingOnly,
  profile,
  external,
}: {
  venue: PublicVenue
  bookingOnly: boolean
  profile: boolean
  external: NonNullable<ReturnType<typeof externalBookingChannel>>
}) {
  if (bookingOnly) return null
  const venueStatus = getVenueOpenStatus(venue)
  if (!profile) return null
  return <RestaurantProfileView
    venue={venue}
    offerings={[]}
    events={[]}
    date={dateInTimeZone(new Date(), venue.timezone ?? 'UTC')}
    party={2}
    options={[]}
    loading={false}
    error=""
    statusLabel={venueStatus.label}
    statusOpen={venueStatus.isOpen}
    favoriteAction={null}
    onSelectOffering={() => undefined}
    onDateChange={() => undefined}
    onPartyChange={() => undefined}
    onBook={() => undefined}
    onBookOffering={() => undefined}
    onPrivateEvent={() => undefined}
    onEvent={() => undefined}
    externalBooking={external}
  />
}

function ConnectedVenue({ venue, bookingIntent, bookingOnly, profile }: { venue: PublicVenue; bookingIntent?: BookingIntent; bookingOnly: boolean; profile: boolean }) {
  const intentOffering = venue.offerings.find((offering) => offering.id === bookingIntent?.experienceId)
  const initialOffering = intentOffering ?? venue.offerings[0]
  const [offerings, setOfferings] = useState(venue.offerings)
  const [events, setEvents] = useState(venue.events)
  const [offeringId, setOfferingId] = useState(intentOffering?.id ?? venue.offerings[0]?.id ?? '')
  const [date, setDate] = useState<string>(() => bookingIntent?.date ?? createDiscoverySearchDefaults(new Date(), venue.timezone ?? 'UTC').date)
  const [party, setParty] = useState(() => clampParty(bookingIntent?.partySize ?? 2, initialOffering))
  const [options, setOptions] = useState<AvailabilityOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeIntent, setActiveIntent] = useState<BookingIntent | undefined>(bookingIntent)
  const [initialBookingOption, setInitialBookingOption] = useState<AvailabilityOption | undefined>()
  const [privateInquiryOpen, setPrivateInquiryOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<PublicEvent | null>(null)
  const [bookingVenue, setBookingVenue] = useState<Venue | null>(() => {
    if (!intentOffering || bookingIntent?.locationSlug !== venue.locationSlug) return null
    return toBookingVenue(venue, intentOffering)
  })
  const favoriteLocations = useFavoriteLocations()
  const activeOffering = offerings.find((offering) => offering.id === offeringId)
  const venueStatus = getVenueOpenStatus(venue)

  const selectOffering = (id: string) => {
    const next = offerings.find((offering) => offering.id === id)
    setOfferingId(id)
    setParty((current) => clampParty(current, next))
  }

  useEffect(() => {
    const controller = new AbortController()
    const loadEligibleOfferings = async () => {
      try {
        const response = await fetch(
          `/api/tables/account/widget/config/${encodeURIComponent(venue.publicWidgetKey)}${venue.accessToken ? `?accessToken=${encodeURIComponent(venue.accessToken)}` : ''}`,
          { signal: controller.signal, cache: 'no-store' },
        )
        if (!response.ok) return
        const personalized = (await response.json()) as PublicVenue
        if (controller.signal.aborted || !Array.isArray(personalized.offerings)) return
        setOfferings(personalized.offerings)
        if (Array.isArray(personalized.events)) setEvents(personalized.events)
        setOfferingId((current) => {
          const intended = personalized.offerings.find((offering) => offering.id === bookingIntent?.experienceId)
          if (intended) return intended.id
          return personalized.offerings.some((offering) => offering.id === current)
            ? current
            : personalized.offerings[0]?.id ?? ''
        })
      } catch {
        // Anonymous visitors keep the public-only server projection.
      }
    }
    void loadEligibleOfferings()
    return () => controller.abort()
  }, [bookingIntent?.experienceId, venue.accessToken, venue.publicWidgetKey])

  useEffect(() => {
    if (!venue.privateEventsEnabled || new URLSearchParams(window.location.search).get('private-events') !== '1') return
    const timer = window.setTimeout(() => setPrivateInquiryOpen(true), 0)
    return () => window.clearTimeout(timer)
  }, [venue.privateEventsEnabled])

  useEffect(() => {
    const eventId = new URLSearchParams(window.location.search).get('eventId')
    const event = events.find((candidate) => candidate.id === eventId)
    if (!event) return
    const timer = window.setTimeout(() => setSelectedEvent(event), 0)
    return () => window.clearTimeout(timer)
  }, [events])

  useEffect(() => {
    if (!offeringId) return
    const controller = new AbortController()
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await fetch('/api/tables/availability/search', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            tenantSlug: venue.tenantSlug,
            locationSlug: venue.locationSlug,
            experienceId: offeringId,
            partySize: party,
            dateFrom: date,
            dateTo: date,
            accessToken: venue.accessToken,
          }),
          signal: controller.signal,
        })
        const payload = await response.json()
        if (!response.ok) throw new Error(payload.message ?? 'Availability could not be loaded.')
        setOptions((payload.dates?.[0]?.options ?? []) as AvailabilityOption[])
      } catch (cause) {
        if (controller.signal.aborted) return
        setOptions([])
        setError(cause instanceof Error ? cause.message : 'Availability could not be loaded.')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    void load()
    return () => controller.abort()
  }, [date, offeringId, party, venue.accessToken, venue.locationSlug, venue.tenantSlug])

  const bookableOptions = useMemo(() => options.filter((option) => option.bookable && option.optionToken), [options])
  const turnMinutes = bookableOptions.find((option) => option.turnMinutes)?.turnMinutes
  const presentationImages = activeOffering?.imageUrls?.length
    ? activeOffering.imageUrls
    : venue.imageUrls ?? []

  function openBooking(option?: AvailabilityOption, source?: 'notify') {
    if (!activeOffering) return
    const launch = createBookingLaunch({
      locationSlug: venue.locationSlug,
      offering: activeOffering,
      date,
      partySize: party,
      option,
      source,
      accessToken: venue.accessToken,
    })
    setActiveIntent(launch.intent)
    setInitialBookingOption(launch.initialOption)
    setBookingVenue(toBookingVenue(venue, activeOffering))
  }

  function openOfferingBooking(offering: PublicOffering) {
    const nextParty = clampParty(party, offering)
    selectOffering(offering.id)
    const nextIntent = { locationSlug: venue.locationSlug, experienceId: offering.id, date, partySize: nextParty, accessToken: venue.accessToken } satisfies BookingIntent
    setActiveIntent(nextIntent)
    setInitialBookingOption(undefined)
    setBookingVenue(toBookingVenue(venue, offering))
  }

  return (
    <>
      {!bookingOnly ? profile ? <RestaurantProfileView
        venue={{ ...venue, offerings, events }}
        offerings={offerings}
        events={events}
        activeOffering={activeOffering}
        date={date}
        party={party}
        options={options}
        loading={loading}
        error={error}
        turnMinutes={turnMinutes}
        statusLabel={venueStatus.label}
        statusOpen={venueStatus.isOpen}
        favoriteAction={<FavoriteVenueButton venueName={venue.publicName} active={favoriteLocations.isFavorite(venue.locationSlug)} busy={favoriteLocations.pending === venue.locationSlug} onToggle={() => void favoriteLocations.toggle(venue.locationSlug)} />}
        onSelectOffering={selectOffering}
        onDateChange={setDate}
        onPartyChange={setParty}
        onBook={openBooking}
        onBookOffering={openOfferingBooking}
        onPrivateEvent={() => setPrivateInquiryOpen(true)}
        onEvent={setSelectedEvent}
      /> : <section className="bookable-section" id="restaurants">
        <div className="featured-venue">
          <div className="featured-venue-media">
            <Image src={presentationImages[0] || venue.imageUrl || venueImage} alt={`${activeOffering?.name ?? venue.publicName} at ${venue.publicName}`} fill sizes="(max-width: 900px) 100vw, 48vw" priority />
            <div className={`venue-status-badge ${venueStatus.isOpen ? 'is-open' : 'is-closed'}`}><span /> {venueStatus.label}</div>
            <div className="featured-venue-caption"><MapPin size={15} /><span>{venue.neighborhood || venue.market || 'Location unavailable'}</span></div>
          </div>

          <div className="featured-venue-content">
            <p className="eyebrow">{venue.cuisine || 'Restaurant'} · Dine in</p>
            <div className="featured-venue-title">{venue.logoUrl ? <Image className="featured-venue-logo" src={venue.logoUrl} alt={`${venue.publicName} logo`} width={52} height={52} /> : null}<h3>{venue.publicName}</h3><FavoriteVenueButton venueName={venue.publicName} active={favoriteLocations.isFavorite(venue.locationSlug)} busy={favoriteLocations.pending === venue.locationSlug} onToggle={() => void favoriteLocations.toggle(venue.locationSlug)} /></div>
            {activeOffering?.description ? <p className="featured-venue-copy">{activeOffering.description}</p> : null}
            {presentationImages.length > 1 ? <div className="experience-gallery-strip" aria-label={`${activeOffering?.name ?? venue.publicName} gallery`}>
              {presentationImages.slice(1, 5).map((url, index) => <Image key={url} src={url} alt={`${activeOffering?.name ?? venue.publicName} image ${index + 2}`} width={86} height={58} />)}
              {presentationImages.length > 5 ? <span>+{presentationImages.length - 5}</span> : null}
            </div> : null}

            {offerings.length > 1 && (
              <div className="offering-tabs" role="tablist" aria-label="Restaurant experiences">
                {offerings.map((offering) => <button role="tab" aria-selected={offering.id === offeringId} key={offering.id} className={offering.id === offeringId ? 'active' : ''} onClick={() => selectOffering(offering.id)}>{offering.name}{offering.access === 'guest_group' ? ' · Members' : offering.access === 'private' ? ' · Private' : ''}</button>)}
              </div>
            )}

            <div className="inline-availability-controls">
              <ReservationDatePicker value={date} min={dateInTimeZone(new Date(), venue.timezone ?? 'UTC')} onChange={setDate} />
              <GuestStepper label="Party size" value={party} min={activeOffering?.partyMin ?? 1} max={activeOffering?.partyMax ?? 8} onChange={setParty} />
            </div>

            <div className="availability-heading">
              <div><strong>{activeOffering?.name ?? 'Reservation'}</strong>{turnMinutes ? <span>{turnMinutes} minutes</span> : null}</div>
              <p aria-live="polite">{loading ? 'Checking availability…' : `${bookableOptions.length} ${bookableOptions.length === 1 ? 'time' : 'times'} available`}</p>
            </div>

            {error && <p className="booking-error" role="alert">{error}</p>}
            {!error && (
              <div className={`inline-time-list ${loading ? 'is-loading' : ''}`} aria-busy={loading}>
                {options.map((option) => {
                  const bookable = option.bookable && Boolean(option.optionToken)
                  const time = formatTime(option.startsAt, venue.timezone)
                  return <button className={bookable ? '' : 'is-notify'} key={option.startsAt} aria-label={`${bookable ? 'Book' : 'Get notified for'} ${activeOffering?.name ?? 'reservation'} at ${time}`} onClick={() => openBooking(option, bookable ? undefined : 'notify')}><span>{time}</span><strong>{bookable ? 'Book' : 'Notify'}</strong></button>
                })}
              </div>
            )}

            {!loading && !error && options.length === 0 && (
              <div className="no-times"><p>No tables match this date and party size.</p><button onClick={() => openBooking(undefined, 'notify')}>Get notified <ArrowRight /></button></div>
            )}

            {activeOffering?.fees?.length ? <div className="experience-fee-notice"><strong>Fees at checkout</strong><span>{activeOffering.fees.map((fee) => `${fee.name} ${formatMoney(fee.priceCents)}`).join(' · ')}</span></div> : null}
            {activeOffering?.menu?.sections.length ? <details className="public-menu-preview"><summary><span><small>Menu</small><strong>{activeOffering.menu.name}</strong></span><ArrowRight /></summary><div>{activeOffering.menu.sections.map((section) => <section key={section.id}><h4>{section.name}</h4>{section.description ? <p>{section.description}</p> : null}{section.items.map((item) => <div key={item.id}><span><strong>{item.name}</strong>{item.description ? <small>{item.description}</small> : null}</span><b>{formatMoney(item.priceCents)}</b></div>)}</section>)}</div></details> : null}
            {venue.privateEventsEnabled && <button className="private-event-action" onClick={() => setPrivateInquiryOpen(true)}>Planning something larger? Send a private-event inquiry <ArrowRight /></button>}
          </div>
        </div>

        <div className="venue-experiences" id="experiences">
          <div className="experience-heading"><h3>Experiences</h3></div>
          <div className="experience-list">
            {offerings.map((offering) => (
              <div className="experience-row" key={offering.id}>
                <div><strong>{offering.name}</strong>{offering.description ? <p>{offering.description}</p> : null}<small>Parties of {offering.partyMin ?? 1}–{offering.partyMax ?? 8}</small></div>
                <button onClick={() => { const nextParty = clampParty(party, offering); selectOffering(offering.id); const nextIntent = { locationSlug: venue.locationSlug, experienceId: offering.id, date, partySize: nextParty, accessToken: venue.accessToken } satisfies BookingIntent; setActiveIntent(nextIntent); setBookingVenue(toBookingVenue(venue, offering)) }}>Book <ArrowRight /></button>
              </div>
            ))}
          </div>
        </div>

        {events.length > 0 && <div className="venue-experiences public-events" id="events">
          <div className="experience-heading"><h3>Events</h3></div>
          <div className="experience-list">
            {events.map((event) => (
              <div className="experience-row" key={event.id}>
                <div><strong>{event.name}</strong><p>{event.description}</p></div>
                <button disabled={!event.occurrences.some((item) => (event.bookingMode === 'table_reservations' || item.remaining > 0) && (item.salesStatus ?? 'open') === 'open')} onClick={() => setSelectedEvent(event)}>{event.bookingMode === "table_reservations" ? "Reserve a table" : "Get tickets"} <ArrowRight /></button>
              </div>
            ))}
          </div>
        </div>}

        <PublicVenueDetails venue={{ ...venue, offerings, events }} />
      </section> : null}

      {bookingVenue && <BookingSheet venue={bookingVenue} initialSearch={activeIntent} initialOption={initialBookingOption} onClose={() => { setActiveIntent(undefined); setInitialBookingOption(undefined); setBookingVenue(null) }} />}
      {venue.privateEventsEnabled && <PrivateEventInquiry open={privateInquiryOpen} onOpenChange={setPrivateInquiryOpen} venue={venue} />}
      <EventCheckoutSheet key={selectedEvent?.id ?? 'closed'} venue={{ ...venue, events }} event={selectedEvent} open={Boolean(selectedEvent)} onOpenChange={(open) => { if (!open) setSelectedEvent(null) }} />
    </>
  )
}

const partySizeOptions = (offering?: PublicOffering) => {
  const minimum = Math.max(1, offering?.partyMin ?? 1)
  const maximum = Math.max(minimum, offering?.partyMax ?? 8)
  return Array.from({ length: maximum - minimum + 1 }, (_, index) => minimum + index)
}

const clampParty = (party: number, offering?: PublicOffering) => {
  const sizes = partySizeOptions(offering)
  return Math.min(sizes.at(-1) ?? 8, Math.max(sizes[0] ?? 1, party))
}

const formatTime = (value: string, timeZone = 'UTC') => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(value))
const formatMoney = (cents: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
